import React from 'react';
import { Link } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { ArrowRight, CheckCircle2 } from 'lucide-react';
import MainLayout from '@/components/layout/MainLayout';
import Header from '@/components/layout/Header';
import CorporateFooter from '@/components/layout/CorporateFooter';
import SEO from '@/components/common/SEO';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Separator } from '@/components/ui/separator';
import { pageLabels, seoPageConfigs, type SeoPageKey } from '@/lib/seo-pages';
import { buildFaqSchema, modelTrainingServiceSchema, softwareApplicationSchema } from '@/lib/seo';

type SeoTopicPageProps = {
  pageKey: SeoPageKey;
};

export default function SeoTopicPage({ pageKey }: SeoTopicPageProps) {
  const page = seoPageConfigs[pageKey];
  const markdownClassName = 'prose prose-sm max-w-none prose-p:my-0 prose-a:font-medium prose-a:no-underline hover:prose-a:underline dark:prose-invert md:prose-base';

  const schemas: Array<Record<string, unknown>> = [buildFaqSchema(page.faqs) as Record<string, unknown>];

  if (page.schemaKinds?.includes('software')) {
    schemas.push(softwareApplicationSchema);
  }

  if (page.schemaKinds?.includes('service')) {
    schemas.push(modelTrainingServiceSchema);
  }

  return (
    <MainLayout>
      <SEO
        title={page.title}
        description={page.description}
        keywords={page.keywords}
        url={page.path}
        schema={schemas}
      />

      <div className="min-h-screen bg-background text-foreground">
        <Header />

        <main className="container mx-auto max-w-6xl px-4 py-12 md:py-20">
          <section className="mx-auto max-w-4xl text-center">
            <Badge variant="outline" className="border-primary/25 bg-primary/5 text-primary">
              {page.badge}
            </Badge>
            <h1 className="mt-6 text-4xl font-semibold tracking-tight md:text-6xl">{page.h1}</h1>
            <div className="mx-auto mt-6 max-w-3xl space-y-4 text-base leading-8 text-muted-foreground md:text-lg">
              {page.intro.map((paragraph) => (
                <div key={paragraph} className={markdownClassName}>
                  <ReactMarkdown remarkPlugins={[remarkGfm]}>{paragraph}</ReactMarkdown>
                </div>
              ))}
            </div>

          </section>

          <section className="mt-12">
            <Card className="border-primary/15 bg-gradient-to-br from-primary/10 via-background to-background shadow-[0_18px_80px_-50px_rgba(220,38,38,0.8)]">
              <CardContent className="p-6 md:p-8">
                <p className="text-xs font-semibold uppercase tracking-[0.25em] text-primary">How CanXP frames this topic</p>
                <div className="mt-6 grid gap-4 md:grid-cols-4">
                  {page.diagramSteps.map((step, index) => (
                    <div key={step} className="rounded-2xl border border-primary/15 bg-background/80 p-4">
                      <div className="text-sm font-semibold text-primary">0{index + 1}</div>
                      <div className="mt-2 text-sm leading-6 text-foreground/90">{step}</div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </section>

          <section className="mt-14 grid gap-6">
            {page.sections.map((section) => (
              <Card key={section.title} className="border-border/70 bg-background/80">
                <CardContent className="p-6 md:p-8">
                  <h2 className="text-2xl font-semibold tracking-tight md:text-3xl">{section.title}</h2>
                  <div className="mt-5 space-y-4 text-sm leading-7 text-muted-foreground md:text-base">
                    {section.paragraphs.map((paragraph) => (
                      <div key={paragraph} className={markdownClassName}>
                        <ReactMarkdown remarkPlugins={[remarkGfm]}>{paragraph}</ReactMarkdown>
                      </div>
                    ))}
                  </div>

                  {section.bullets?.length ? (
                    <div className="mt-6 grid gap-3 md:grid-cols-2">
                      {section.bullets.map((bullet) => (
                        <div key={bullet} className="flex gap-3 rounded-xl border border-border/70 bg-muted/20 p-4">
                          <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0 text-primary" />
                          <span className="text-sm leading-6 text-foreground/90">{bullet}</span>
                        </div>
                      ))}
                    </div>
                  ) : null}
                </CardContent>
              </Card>
            ))}
          </section>

          <Separator className="my-14" />

          <section className="grid gap-8 lg:grid-cols-[1.2fr_0.8fr]">
            <Card className="border-primary/15 bg-card/70">
              <CardContent className="p-6 md:p-8">
                <p className="text-xs font-semibold uppercase tracking-[0.25em] text-primary">Frequently asked questions</p>
                <h2 className="mt-4 text-2xl font-semibold tracking-tight md:text-3xl">Questions buyers commonly ask</h2>
                <Accordion type="single" collapsible className="mt-6 w-full">
                  {page.faqs.map((faq, index) => (
                    <AccordionItem key={faq.question} value={`item-${index}`}>
                      <AccordionTrigger className="text-left">{faq.question}</AccordionTrigger>
                      <AccordionContent className="text-sm leading-7 text-muted-foreground">
                        {faq.answer}
                      </AccordionContent>
                    </AccordionItem>
                  ))}
                </Accordion>
              </CardContent>
            </Card>

            <div className="space-y-6">
              <Card className="border-primary/15 bg-card/70">
                <CardContent className="p-6 md:p-8">
                  <p className="text-xs font-semibold uppercase tracking-[0.25em] text-primary">Related pages</p>
                  <h2 className="mt-4 text-2xl font-semibold tracking-tight">Explore the CanXP AI architecture</h2>
                  <div className="mt-6 space-y-3">
                    {page.relatedLinks.map((href) => (
                      <Link
                        key={href}
                        to={href}
                        className="flex items-center justify-between rounded-xl border border-border/70 bg-background/80 px-4 py-3 text-sm font-medium transition-colors hover:border-primary/30 hover:text-primary"
                      >
                        <span>{pageLabels[href] || href}</span>
                        <ArrowRight className="h-4 w-4" />
                      </Link>
                    ))}
                  </div>
                </CardContent>
              </Card>

              <Card className="border-primary/20 bg-gradient-to-br from-primary/10 via-background to-background shadow-[0_18px_80px_-50px_rgba(220,38,38,0.8)]">
                <CardContent className="p-6 md:p-8">
                  <p className="text-xs font-semibold uppercase tracking-[0.25em] text-primary">Next step</p>
                  <h2 className="mt-4 text-2xl font-semibold tracking-tight">{page.ctaTitle}</h2>
                  <p className="mt-4 text-sm leading-7 text-muted-foreground">{page.ctaBody}</p>
                  <Button asChild className="mt-6 gap-2">
                    <Link to={page.ctaHref}>
                      {page.ctaLabel}
                      <ArrowRight className="h-4 w-4" />
                    </Link>
                  </Button>
                </CardContent>
              </Card>
            </div>
          </section>
        </main>

        <CorporateFooter />
      </div>
    </MainLayout>
  );
}
